import { type IHitArea, Rectangle } from "pixi.js";
import type { MapCoordinates } from "./MapCoordinates";
import type { ImageOverlayMapObject, Point2D } from "./types";

export type ImageOverlayQuad = readonly [Point2D, Point2D, Point2D, Point2D];

export interface ImageOverlayGeometry {
    item: ImageOverlayMapObject;
    worldCorners: ImageOverlayQuad;
    sceneCorners: ImageOverlayQuad;
    worldCenter: Point2D;
}

export interface ImageOverlaySceneGeometry {
    sceneCorners: ImageOverlayQuad;
    cullBounds: Rectangle;
}

const isFinitePoint = (point: Point2D): boolean =>
    Number.isFinite(point.x) && Number.isFinite(point.y);

export const getImageOverlayCenter = (corners: ImageOverlayQuad): Point2D => {
    // A projected image center is the intersection of the two projected diagonals.
    const firstDirection = {
        x: corners[2].x - corners[0].x,
        y: corners[2].y - corners[0].y,
    };
    const secondDirection = {
        x: corners[3].x - corners[1].x,
        y: corners[3].y - corners[1].y,
    };
    const offset = {
        x: corners[1].x - corners[0].x,
        y: corners[1].y - corners[0].y,
    };
    const denominator =
        firstDirection.x * secondDirection.y -
        firstDirection.y * secondDirection.x;

    if (Math.abs(denominator) <= Number.EPSILON) {
        return {
            x:
                corners.reduce((sum, corner) => sum + corner.x, 0) /
                corners.length,
            y:
                corners.reduce((sum, corner) => sum + corner.y, 0) /
                corners.length,
        };
    }

    const firstRatio =
        (offset.x * secondDirection.y - offset.y * secondDirection.x) /
        denominator;

    return {
        x: corners[0].x + firstRatio * firstDirection.x,
        y: corners[0].y + firstRatio * firstDirection.y,
    };
};

export const projectImageOverlayToScene = (
    corners: ImageOverlayQuad,
    coordinates: MapCoordinates,
): ImageOverlaySceneGeometry | null => {
    if (!corners.every(isFinitePoint)) return null;

    const sceneCorners: ImageOverlayQuad = [
        coordinates.worldToScene(corners[0]),
        coordinates.worldToScene(corners[1]),
        coordinates.worldToScene(corners[2]),
        coordinates.worldToScene(corners[3]),
    ];
    const xValues = sceneCorners.map((corner) => corner.x);
    const yValues = sceneCorners.map((corner) => corner.y);
    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    return {
        sceneCorners,
        cullBounds: new Rectangle(xMin, yMin, xMax - xMin, yMax - yMin),
    };
};

const isPointOnSegment = (
    point: Point2D,
    start: Point2D,
    end: Point2D,
): boolean => {
    const xLength = end.x - start.x;
    const yLength = end.y - start.y;
    const lengthSquared = xLength * xLength + yLength * yLength;

    if (lengthSquared === 0) {
        return point.x === start.x && point.y === start.y;
    }

    const cross = (point.y - start.y) * xLength - (point.x - start.x) * yLength;
    if (Math.abs(cross) > Number.EPSILON) return false;

    const dot = (point.x - start.x) * xLength + (point.y - start.y) * yLength;
    if (dot < 0) return false;

    return dot <= lengthSquared;
};

const containsPoint = (corners: ImageOverlayQuad, point: Point2D): boolean => {
    let inside = false;

    for (
        let index = 0, previous = corners.length - 1;
        index < corners.length;
        previous = index++
    ) {
        const start = corners[previous];
        const end = corners[index];

        if (isPointOnSegment(point, start, end)) return true;

        const intersects =
            start.y > point.y !== end.y > point.y &&
            point.x <
                ((end.x - start.x) * (point.y - start.y)) / (end.y - start.y) +
                    start.x;

        if (intersects) inside = !inside;
    }

    return inside;
};

export const findNearestContainingOverlay = (
    geometries: readonly ImageOverlayGeometry[],
    point: Point2D,
): ImageOverlayGeometry | undefined => {
    let nearest: ImageOverlayGeometry | undefined;
    let nearestDistanceSquared = Infinity;

    for (const geometry of geometries) {
        if (!containsPoint(geometry.worldCorners, point)) continue;

        const xDistance = geometry.worldCenter.x - point.x;
        const yDistance = geometry.worldCenter.y - point.y;
        const distanceSquared = xDistance * xDistance + yDistance * yDistance;

        if (distanceSquared < nearestDistanceSquared) {
            nearest = geometry;
            nearestDistanceSquared = distanceSquared;
        }
    }

    return nearest;
};

export class ImageOverlaysHitArea implements IHitArea {
    constructor(private readonly footprints: readonly ImageOverlayQuad[]) {}

    contains(x: number, y: number): boolean {
        return this.footprints.some((footprint) =>
            containsPoint(footprint, { x, y }),
        );
    }
}
